import { Badge, Checkbox } from '@/components/ui';
import { cn } from '@/utils/cn';

/**
 * One group of suggestions read out of a CV.
 *
 * Three states, drawn differently on purpose. A suggestion you can take gets a
 * tick box. One you already have is greyed out and says so -- that is not a
 * failure and should not read like one. One we could not read enough of names
 * the field that is missing, because "we could not read this" without saying
 * what is missing leaves the student with nothing to do about it.
 */
export function ProposalList({ title, items, selected, onToggle, render, empty }) {
  if (items.length === 0) {
    return (
      <section>
        <h3 className="text-ink mb-2 text-sm font-medium">{title}</h3>
        <p className="text-ink-muted text-sm">{empty}</p>
      </section>
    );
  }

  return (
    <section>
      <h3 className="text-ink mb-2 text-sm font-medium">{title}</h3>
      <ul className="divide-line divide-y">
        {items.map((item) => {
          const blocked = item.already_on_profile || !item.is_complete;
          return (
            <li
              key={item.index}
              className={cn('flex items-start gap-3 py-3', blocked && 'opacity-60')}
            >
              <div className="pt-0.5">
                {blocked ? (
                  <span className="border-line block size-4 rounded border" aria-hidden />
                ) : (
                  <Checkbox
                    checked={selected.includes(item.index)}
                    onChange={() => onToggle(item.index)}
                    aria-label={`Add ${render(item).heading}`}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <ProposalBody item={item} render={render} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ProposalBody({ item, render }) {
  const { heading, subheading, detail } = render(item);
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p className="text-ink text-sm font-medium">{heading}</p>
        {item.already_on_profile && <Badge>Already on your profile</Badge>}
        {!item.is_complete && <Badge tone="warning">Missing {item.missing.join(' and ')}</Badge>}
      </div>
      {subheading && <p className="text-ink-muted mt-0.5 text-sm">{subheading}</p>}
      {detail && <p className="text-ink-muted text-caption mt-0.5">{detail}</p>}
    </>
  );
}
